import { Check, SubtitlesOff, SubtitlesOn, useDropdownMenu } from '@core/ui';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

interface SubtitlesMenuProps {
    trackIds: string[];
    activeSubtitleTrackId?: string | null;
    onSelect: (trackId: string) => void;
}

/** Subtitles button and track menu, shared by the live and the on-demand player. */
export const SubtitlesMenu = ({ trackIds, activeSubtitleTrackId, onSelect }: SubtitlesMenuProps) => {
    const { t } = useTranslation();
    const { open, toggleMenu, closeMenu, triggerRef, menuRef, registerItem } = useDropdownMenu();
    const menuId = useId();

    const handleSubtitleMenuClick = (trackId: string) => {
        onSelect(trackId);
        closeMenu();
    };

    if (trackIds.length === 0) {
        return null;
    }

    // An empty track id switches subtitles off
    const options = [{ trackId: '', label: t('Player.SubtitleSelect.NoSubtitles') }, ...trackIds.map((trackId) => ({ trackId, label: trackId }))];

    return (
        <>
            <button
                ref={triggerRef}
                className="group size-12 rounded-md cursor-pointer p-1 text-white"
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                {...(open && { 'aria-controls': menuId })}
                onClick={toggleMenu}
            >
                <span className="sr-only">{t('Player.Button.Subtitles')}</span>
                <span className="block bg-primary/50 rounded-md p-2 group-hover:bg-primary/95 transition-color duration-200 ease-in-out">
                    {activeSubtitleTrackId ? <SubtitlesOn classNames="fill-white" /> : <SubtitlesOff classNames="stroke-white" />}
                </span>
            </button>
            {open && (
                <div ref={menuRef} id={menuId} className="absolute right-0 min-w-48 max-w-70 bg-white rounded-md shadow-xl py-2" role="menu" tabIndex={-1}>
                    {options.map(({ trackId, label }) => {
                        const checked = trackId === (activeSubtitleTrackId ?? '');
                        return (
                            <button
                                ref={registerItem}
                                className={`w-full hover:bg-primary focus-visible:bg-primary hover:text-on-primary focus-visible:text-on-primary cursor-pointer flex items-center text-sm text-left ${checked ? 'ps-4' : 'ps-10'} pe-4 py-2 select-none transition-colors ease-in-out duration-200`}
                                key={trackId}
                                role="menuitemradio"
                                aria-checked={checked}
                                tabIndex={0}
                                onClick={() => handleSubtitleMenuClick(trackId)}
                            >
                                {checked && <Check classNames="size-4 flex-none stroke-current me-2" />}
                                {label}
                            </button>
                        );
                    })}
                </div>
            )}
        </>
    );
};
